import {
  WSButton,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSTableItem,
  WSTableToolbar,
  WSText,
  toCamelCase,
  useModalContext
} from "@wingspanhq/fe-component-library";
import {
  IInvoice,
  IInvoiceTemplate
} from "@wingspanhq/payments/dist/interfaces";
import React, { ReactNode, useMemo, useState } from "react";
import { BulkAcceptModal, INVOICES_TABLE_BULK_ACCEPT } from "./BulkAcceptModal";
import {
  BulkDisputeModal,
  INVOICES_TABLE_BULK_DISPUTE
} from "./BulkDisputeModal";
import styles from "./InvoicesTable.module.scss";
import { InvoicesTableItem, Table } from "./Table";

const pageSize = 20;

type Props = {
  title?: string;
  invoices?: IInvoice[];
  invoiceTemplates?: IInvoiceTemplate[];
  emptyState?: ReactNode;
  listName?: "paid" | "list";
  sort?: (a: InvoicesTableItem, b: InvoicesTableItem) => number;
  pageLimit?: number;
  bulkActions?: ("accept" | "dispute")[];
  actions?: ReactNode;
} & WSElementProps;

export const InvoicesTable: React.FC<Props> = ({
  title,
  invoices = [],
  invoiceTemplates = [],
  emptyState,
  listName,
  bulkActions,
  actions,
  ...elementProps
}) => {
  const { openModal } = useModalContext();
  const [limit, setLimit] = useState(pageSize);
  const [selection, setSelection] = useState<WSTableItem[]>([]);

  const showBulkActions = bulkActions && bulkActions.length > 0;

  let data: InvoicesTableItem[] = [
    ...invoices.map(invoice => ({
      id: invoice.invoiceId,
      data: {
        updatedAt: invoice.updatedAt,
        invoice
      }
    })),
    ...invoiceTemplates.map(invoiceTemplate => ({
      id: invoiceTemplate.invoiceTemplateId,
      data: {
        updatedAt: invoiceTemplate.updatedAt,
        invoiceTemplate
      }
    }))
  ];

  const tableData = data.slice(0, limit);

  const toolbarActions = useMemo(() => {
    return (bulkActions || []).map(action => {
      switch (action) {
        case "dispute":
          return {
            label: "Dispute",
            onClick: () => {
              openModal(INVOICES_TABLE_BULK_DISPUTE, {
                data: selection.map(item => item.data),
                onFinish: () => {
                  setSelection([]);
                }
              });
            }
          };
        case "accept":
        default:
          return {
            label: "Accept",
            onClick: () => {
              openModal(INVOICES_TABLE_BULK_ACCEPT, {
                data: selection.map(item => item.data),
                onFinish: () => {
                  setSelection([]);
                }
              });
            }
          };
      }
    });
  }, [bulkActions, openModal, selection]);

  return (
    <WSElement {...elementProps}>
      {data.length > 0 ? (
        <>
          <WSFlexBox.CenterY wrap="nowrap" className={styles.headerWrapper}>
            {title && (
              <WSText.Heading5 className={styles.header} mb="XL">
                {title}
              </WSText.Heading5>
            )}

            <WSElement>{actions}</WSElement>
          </WSFlexBox.CenterY>

          {showBulkActions && data.length > 0 && (
            <WSTableToolbar
              count={selection.length}
              onClear={() => {
                setSelection([]);
              }}
              actions={toolbarActions}
            />
          )}

          <Table
            listName={listName}
            tableData={tableData}
            selection={selection}
            onSelectionChange={setSelection}
            data-testid={toCamelCase(title || "", "invoices", "table")}
          />

          {data.length > limit && (
            <WSFlexBox.CenterX mt="XL">
              <WSButton.Link
                onClick={() => {
                  setLimit(limit => limit + pageSize);
                }}
              >
                Show more
              </WSButton.Link>
            </WSFlexBox.CenterX>
          )}
        </>
      ) : emptyState ? (
        emptyState
      ) : null}

      <BulkAcceptModal />
      <BulkDisputeModal />
    </WSElement>
  );
};
